import type { Article, BlogPosting, NewsArticle, Organization, Person } from 'schema-dts';
import type { ButterflyData, ButterflyPost, ButterflyRelation } from '$lib/types';
import getWebPage from './getWebPage';
import getPublisher from './getPublisher';
import getMediaUrl from '$lib/getMediaUrl';
import { siteUrl } from '$lib/config';

function getType(post: ButterflyPost): 'Article' | 'NewsArticle' | 'BlogPosting' {
  if (post.attributes.flags.indexOf('news') >= 0) {
    return 'NewsArticle';
  }
  if (post.attributes.flags.indexOf('blog') >= 0) {
    return 'BlogPosting';
  }
  return 'Article';
}

export default function getArticle(data: App.PageData): Article | NewsArticle | BlogPosting {
  const scheme: Article | NewsArticle | BlogPosting = {
    '@type': getType(data.scope.data.data),
    '@id': `${data.scope.meta.canonical}#scope`,
    headline: data.scope.data.data.attributes.title,
    description: data.scope.data.data.attributes.resume,
    mainEntityOfPage: getWebPage(data),
    datePublished: data.scope.data.data.attributes.publishedAt,
    url: data.scope.meta.canonical,
    publisher: getPublisher(),
  };

  if (data.scope.data.data.attributes.publishedAt < data.scope.data.data.attributes.updatedAt) {
    scheme.dateModified = data.scope.data.data.attributes.updatedAt;
  }

  if (data.scope.data.data.relationships.thumbnail.data?.id) {
    const media = data.scope.data.included.find(
      (i: ButterflyData) =>
        i.id === data.scope.data.data.relationships.thumbnail.data.id &&
        i.type === data.scope.data.data.relationships.thumbnail.data.type,
    );
    scheme.image = [getMediaUrl(media, { format: 'thumb', ext: 'jpg' })];
  }

  if (data.scope.data.data.relationships.authors.data.length) {
    scheme.author = data.scope.data.data.relationships.authors.data.map((a: ButterflyRelation) => {
      const author = data.scope.data.included.find(
        (i: ButterflyData) => i.id === a.id && i.type === a.type,
      );
      const authorLd: Person | Organization = {
        '@type': author.attributes.type === 'person' ? 'Person' : 'Organization',
        name: author.attributes.name,
        url: `${siteUrl}/authors/${author.id}`,
      };

      return authorLd;
    });
  }

  return scheme;
}
